import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as uiActions from "store/actions/uiActions";

import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ErrorMessage from "./ErrorMessage";

import storage from "@storage";
import i18next, { loadAllForLanguage, supportedLanguages } from "locale";
import { baseURL } from "config/environment";

const Theme = ({ children }) => {
  const dispatch = useDispatch();
  const email = useSelector(state => state.auth?.user?.email);
  const isLoading = useSelector(state => state.ui.theme.isLoading);
  const title = useSelector(state => state.ui.theme.title);
  const metaDescription = useSelector(state => state.ui.theme.metaDescription);
  const favicon = useSelector(state => state.ui.theme.favicon);
  const logo = useSelector(state => state.ui.theme.logo);
  const darkLogo = useSelector(state => state.ui.theme.darkLogo);
  const defaultLanguage = useSelector(state => state.ui.theme.language);
  const errorMessage = useSelector(state => state.ui.theme.errorMessage);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(
      uiActions.setTheme(
        window.location.pathname !== "/login" &&
          window.location.pathname !== "/logout"
          ? email
          : ""
      )
    );
  }, [dispatch, email]);

  useEffect(() => {
    const head = document.getElementsByTagName("head")[0];
    let meta = head.querySelector('meta[name="description"]');

    if (
      title &&
      window.location.pathname !== "/login" &&
      window.location.pathname !== "/logout"
    ) {
      document.title = title;
    }

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      head.appendChild(meta);
    }

    meta.setAttribute(
      "content",
      metaDescription || title || "Webmail Application"
    );
  }, [title, metaDescription]);

  useEffect(() => {
    if (!isLoading) {
      const navigatorLanguage = navigator.language?.split("-")[0] || "en";
      let language = defaultLanguage?.[0];
      if (!language && supportedLanguages.includes(navigatorLanguage)) {
        language = navigatorLanguage;
      }

      if (!storage.get("lang") && language) {
        loadAllForLanguage(language)
          .then(() => i18next.changeLanguage(language))
          .then(() => storage.set("lang", language));
      }
      document.documentElement.lang = language;

      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        link.type = "image/x-icon";
        document.getElementsByTagName("head")[0].appendChild(link);
      }
      link.href = favicon || "/favicon.ico";

      const safariLink = document.createElement("link");
      safariLink.rel = "apple-touch-icon";
      safariLink.type = "image/x-icon";
      document.getElementsByTagName("head")[0].appendChild(safariLink);
      safariLink.href = favicon || "favicon.ico";

      const safariTabLink = document.createElement("link");
      safariTabLink.rel = "mask-icon";
      safariTabLink.type = "image/x-icon";
      document.getElementsByTagName("head")[0].appendChild(safariTabLink);
      safariTabLink.href = favicon || "favicon.ico";
      setLoading(false);
    }
  }, [defaultLanguage, isLoading, favicon]);

  useEffect(() => {
    if (isLoading) return;

    const rawPath = logo || darkLogo;
    if (!rawPath) return;

    const href =
      rawPath.includes("http") || rawPath.startsWith("data:")
        ? rawPath
        : baseURL + rawPath;

    const head = document.getElementsByTagName("head")[0];
    const existing = document.getElementById("mx-theme-logo-preload");

    const link = existing || document.createElement("link");
    link.setAttribute("id", "mx-theme-logo-preload");
    link.setAttribute("rel", "preload");
    link.setAttribute("as", "image");
    link.setAttribute("fetchpriority", "high");
    link.setAttribute("href", href);

    if (!existing) {
      head.insertBefore(link, head.firstChild);
    }
  }, [isLoading, logo, darkLogo]);

  const renderLoading = () => (
    <div
      style={{ height: "100vh" }}
      className="d-flex justify-content-center align-items-center"
    >
      <FontAwesomeIcon
        icon="circle-notch"
        size="lg"
        spin
        data-testid="theme-spinner"
      />
    </div>
  );

  if (errorMessage && window.location.pathname !== "/maintenance") {
    return <ErrorMessage errorMessage={errorMessage} />;
  }

  return isLoading && loading ? renderLoading() : children;
};

Theme.propTypes = {
  children: PropTypes.element
};

export default Theme;
